// frontend/src/components/PrioritiseModal.js

import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { LucideSave, ChevronDown, ChevronUp, BookX, X } from 'lucide-react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css'; // Import the CSS for styling
import { useTranslation } from 'react-i18next';

import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';

const PrioritiseModal = ({ 
  selectedSchedule, 
  conflicts, 
  scheduleMap, 
  onClose, 
  onUpdatePriority, 
  getScheduleSnapshot 
}) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  useDateTimePreferences();

  // Combine the selected schedule with its conflicts
  const [prioritiseList, setPrioritiseList] = useState([]);
  
    // State to hold the initial order for change detection
  const [initialPrioritiseOrder, setInitialPrioritiseOrder] = useState([]);
    // Track if changes have been made
  const [hasChanges, setHasChanges] = useState(false);
  const userId = parseInt(localStorage.getItem('userId'), 10);
  console.log('selectedSchedule:', selectedSchedule);
  console.log('conflicts:', conflicts);
  console.log('scheduleMap:', scheduleMap);

  // Helper function to format included days
  const formatIncludedDays = (includedDays) => {
      if (!includedDays) return 'N/A';
      
      // Assuming 'included_days' is a comma-separated string like "0,1,2,3,4,5,6"
      const daysMap = {
        0: 'Sun',
        1: 'Mon',
        2: 'Tue',
        3: 'Wed',
        4: 'Thu',
        5: 'Fri',
        6: 'Sat'
      };
      
      const daysArray = includedDays.split(',').map(day => day.trim()).filter(day => day !== '');
      
      return daysArray.map(day => `${day}-${daysMap[day]}`).join('; ');
    };
  
  useEffect(() => {
    // Create a list including the selected schedule and its conflicts
    const conflictSchedules = conflicts.map(conflictId => scheduleMap[conflictId]).filter(Boolean);

    // Check if the selected schedule is already in the conflict list to avoid duplication
    const isSelectedInConflicts = conflictSchedules.some(conflict => conflict.id === selectedSchedule.id);

    const combinedList = isSelectedInConflicts
      ? conflictSchedules
      : [selectedSchedule, ...conflictSchedules];

    // Sort the combined list by current priority (ascending)
    combinedList.sort((a, b) => a.priority - b.priority);

    setPrioritiseList(combinedList);

    // Store the initial order of schedule IDs for comparison
    setInitialPrioritiseOrder(combinedList.map(schedule => schedule.id));

  }, [selectedSchedule, conflicts, scheduleMap]);

  // Effect to detect changes by comparing current prioritiseList with initialPrioritiseOrder
  useEffect(() => {
    // Ensure both lists are initialized
    console.log('selectedSchedule Priority:', selectedSchedule.priority);
    console.log('prioritiseList:', prioritiseList);
    console.log('initialPrioritiseOrder:', initialPrioritiseOrder);
    if (initialPrioritiseOrder.length === 0 || prioritiseList.length === 0) {
      setHasChanges(false);
      return;
    }

    // Generate current order of schedule IDs
    const currentOrder = prioritiseList.map(schedule => schedule.id);

    // Check if current order differs from initial order
    const isDifferent = currentOrder.some((id, index) => id !== initialPrioritiseOrder[index]);

    setHasChanges(isDifferent);
  }, [prioritiseList, initialPrioritiseOrder]);

  // Handle moving a schedule up in priority
  const moveUp = (index) => {
    if (index === 0) return; // Already at top
    setPrioritiseList(prevList => {
      const newList = [...prevList];
      [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
      return newList;
    });
  };

  // Handle moving a schedule down in priority
  const moveDown = (index) => {
    if (index === prioritiseList.length - 1) return; // Already at bottom
    setPrioritiseList(prevList => {
      const newList = [...prevList];
      [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
      return newList;
    });
  };

  // Save the updated priorities
  const savePriorities = async () => {
    try {
      // Create array of promises for updates that need to happen
      const updatePromises = prioritiseList.map((scheduleItem, index) => {
        const newPriority = index;
        if (scheduleItem.priority !== newPriority) {
          return onUpdatePriority(scheduleItem.id, newPriority);
        }
        return null;
      }).filter(Boolean); // Remove null values
  
      // Wait for all updates to complete
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        toast.success(t('Priorities updated successfully!'));
      }
  
      setInitialPrioritiseOrder(prioritiseList.map(schedule => schedule.id));
      setHasChanges(false);
      onClose();
  
    } catch (error) {
      toast.error(t('Failed to update priorities'));
      console.error('Error updating priorities:', error);
    }
  };
  

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={cn(
        "rounded-lg w-full max-w-2xl mx-4 shadow-xl flex flex-col max-h-[80vh]",
        background.primary,
        border.default,
        "border"
      )}>
        {/* Header */}
        <div className={cn(
          "flex items-center justify-between p-6",
          border.default,
          "border-b"
        )}>
          <h2 className={cn("text-xl font-bold", text.primary)}>
            {t('Prioritise Conflicting Schedules')}
          </h2>
          <button
            onClick={onClose}
            className={cn(
              "p-2 rounded-lg transition-colors",
              "hover:bg-gray-100 dark:hover:bg-gray-700",
              text.secondary
            )}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-6">
        <ul className="space-y-3">
          {prioritiseList.map((scheduleItem, index) => (
            <li key={scheduleItem.id} className={cn(
              "flex items-center justify-between p-4 rounded-lg border transition-colors",
              "border-gray-200 dark:border-gray-700",
              "bg-gray-50 dark:bg-gray-800/50"
            )}>
              <div className="flex-grow">
                {/* Wrap the entire content with Tippy tooltip */}
                <Tippy
                  content={
                    <div>
                      <p><strong>{t('Schedule Name')}:</strong> {scheduleItem.name}</p>
                      
                      {/* Include Snapshot */}    
                      <p><strong>{scheduleItem.created_by === userId ? 'M' : 'O'}</strong> : <strong>M:</strong> Created by Me;  <strong>O:</strong> by Other User</p>                  
                      <p><strong>OC:</strong> On-Call, <strong>F:</strong> Fixed</p>
                      <p><strong>Date:</strong> From {formatDate(scheduleItem.start_date)} to {formatDate(scheduleItem.end_date)}</p>
                      <p><strong>Days:</strong> {formatIncludedDays(scheduleItem.included_days)}</p>
                      <p><strong>S:</strong> Single trip, <strong>R:</strong> Recurring trip</p>
                      <p><strong>Excl P:</strong> Excluded periods</p>
                      <p><strong>{t('Priority')}:</strong> {`${scheduleItem.priority} : 0-Highest,1-Second Highest, ...`}</p>
                    </div>
                  }
                  placement="right"
                  theme="dark"
                  arrow={true}
                >
                  <div className="cursor-pointer">
                    {/* Schedule Name with Conditional Line-Through */}
                    <span className={cn("font-semibold", text.primary)}>
                      {scheduleItem.name}
                    </span>
                    
                    {/* Snapshot Information */}
                    <span className={cn("text-sm ml-2", text.muted)}>
                      {getScheduleSnapshot(scheduleItem)}
                    </span>                    
                  </div>
                </Tippy>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => moveUp(index)}
                  className={cn(
                    "p-2 rounded-lg transition-colors",
                    index === 0 
                      ? "bg-gray-300 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400" 
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  )}
                  disabled={index === 0}
                  aria-label={t('Move Up')}
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  onClick={() => moveDown(index)}
                  className={cn(
                    "p-2 rounded-lg transition-colors",
                    index === prioritiseList.length - 1
                      ? "bg-gray-300 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  )}
                  disabled={index === prioritiseList.length - 1}
                  aria-label={t('Move Down')}
                >
                  <ChevronDown size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
        </div>

        {/* Footer */}
        <div className={cn(
          "flex justify-end items-center gap-3 p-6",
          border.default,
          "border-t"
        )}>
          <button
            onClick={savePriorities}
            disabled={!hasChanges}
            className={cn(
              "flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors shadow-sm",
              hasChanges
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            )}
          >            
            <LucideSave size={16} />
            {t('Update Priorities')}
          </button>
          <button
            onClick={onClose}
            className={cn(
              "flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors shadow-sm",
              "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600",
              "text-gray-800 dark:text-gray-200"
            )}
          >
            <BookX size={16} />
            {t('Cancel')}
          </button>          
        </div>
      </div>
    </div>
  );
};


export default PrioritiseModal;
